import {
  Box,
  Button,
  CircularProgress,
  Divider,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import { useObserver } from 'mobx-react-lite';
import React, { FC, useEffect } from 'react';
import { useAppleWalletStore } from '../../stores/AppleWalletStore';
import ViewStateContainer from '../Core/ViewStateContainer/ViewStateContainer';
import mugSaucer from '../../static/images/mug-saucer-solid.svg';
import CurrencyFormat from '../Core/CurrencyFormat/CurrencyFormat';
import OrderSummary from '../OrderingPage/OrderDetail/OrderSummary';
import { loadScript } from '../../core/ScriptUtils';
import OnlineOrderingRepository from '../../interfaces/OnlineOrderingRepository';
import { Alert } from '@material-ui/lab';
import { ViewState } from '../../domain/models/ViewState';
import { useOrdering } from '../../stores/OrderingStore';
import { SaveOrderAsyncViewState } from '../../domain/models/SaveOrderViewState';

type PayloadPayment = {
  tokenType: string;
  token: string;
  wallet: {
    cardDetails: string;
  };
};

const useStyles = makeStyles((theme: Theme) => {
  const tbHeight =
    typeof theme.mixins.toolbar.minHeight === 'number'
      ? theme.mixins.toolbar.minHeight
      : 0;

  return {
    content: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      overflowY: 'auto',
      overflowX: 'hidden',
      height: `calc(100% - ${tbHeight + theme.spacing(4)}px)`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    divider: {
      marginBottom: 15,
      borderTop: '1px solid black'
    },
    image: {
      height: 50,
      marginBottom: 15
    }
  };
});

const AppleWalletPage: FC<{
  onlineOrderingRepository: OnlineOrderingRepository;
}> = ({ onlineOrderingRepository }) => {
  const classes = useStyles();
  const appleWalletStore = useAppleWalletStore();
  const orderStore = useOrdering();
  const order = appleWalletStore.order!;
  const store = appleWalletStore.store!;
  const isSandbox = store?.is_sandbox;
  const submitAppleWallet = ({
    token,
    number
  }: {
    token: string;
    number: string;
  }) => {
    orderStore.submitState = SaveOrderAsyncViewState.AWAITING_PAYMENT;
    return onlineOrderingRepository
      .submitAppleWallet(store.id!, appleWalletStore.orderId!, {
        token,
        number
      })
      .catch(error => {
        orderStore.submitState = ViewState.ERROR;
        orderStore.submitErrorMessage =
          error.response?.data?.message ?? String(error);
      });
  };

  useEffect(() => {
    order?.id && orderStore.listenForPayingStatus(order.id, false);
  }, [orderStore, order?.id]);

  useEffect(() => {
    if (appleWalletStore.viewState !== ViewState.OK || isSandbox) return;
    const script = loadScript(
      'https://chachapos.transactiongateway.com/token/Collect.js',
      document.querySelector('head'),
      'apple-wallet',
      {
        'data-tokenization-key': store.apple_wallet_token as string
      }
    );
    script!.onload = () => {
      const collectJs = (window as any).CollectJS;
      collectJs.configure({
        variant: 'inline',
        country: 'US',
        price: String(order?.total),
        currency: 'USD',
        callback: (data: PayloadPayment) =>
          submitAppleWallet({
            token: data.token,
            number: data.wallet.cardDetails
          })
      });
    };

    return () => {
      document.body.removeChild(script!);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appleWalletStore.viewState]);

  const notifyPayment = () => {
    const status = String(orderStore.submitState);
    const alertObject = {
      [String(ViewState.OK)]: (
        <Alert severity={'success'}>Order has succeeded</Alert>
      ),
      [String(ViewState.ERROR)]: (
        <Alert severity={'error'}>{orderStore.submitErrorMessage}</Alert>
      )
    };

    return alertObject[status] ?? null;
  };

  return useObserver(() => (
    <Box key="content" className={classes.content}>
      <ViewStateContainer
        state={appleWalletStore.viewState}
        onReload={() => appleWalletStore.reload()}
      >
        <Box width={'500px'} display={'flex'} flexDirection={'column'}>
          <img alt="icon" className={classes.image} src={mugSaucer} />
          <Typography variant={'h3'} align="center">
            <CurrencyFormat amount={order?.total} />
          </Typography>
          <Typography variant="h6" color="textSecondary" align="center">
            {store?.name}
          </Typography>
          <Divider className={classes.divider} />
          <Box>
            <Typography align="center" variant="h5">
              Transaction details
            </Typography>
            <Box margin={'50px'}>
              <OrderSummary order={order} store={store} />
            </Box>

            {isSandbox ? (
              !orderStore.submitState && (
                <Box display="flex" justifyContent="center">
                  <Button
                    onClick={() =>
                      submitAppleWallet({
                        token: 'sandbox',
                        number: '1234'
                      })
                    }
                  >
                    Pay with Apple Wallet
                  </Button>
                </Box>
              )
            ) : (
              <div id="applepaybutton"></div>
            )}
            {[
              SaveOrderAsyncViewState.PAYING,
              SaveOrderAsyncViewState.AWAITING_PAYMENT
            ].includes(orderStore.submitState as SaveOrderAsyncViewState) ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : (
              notifyPayment()
            )}
          </Box>
        </Box>
      </ViewStateContainer>
    </Box>
  ));
};

export default AppleWalletPage;
